import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  CircularProgress,
  Stack,
  InputAdornment,
  IconButton,
  Fade,
} from "@mui/material";
import {
  PhoneAndroidOutlined,
  LoginOutlined,
  EditRounded,
  TimerOutlined,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { ArrowForwardRounded } from "@mui/icons-material";


export default function OTPLoginUI({
  step,
  phone,
  setPhone,
  code,
  setCode,
  loading,
  error,
  timer,
  locked,
  lockSeconds,
  codeReplaced,
  requestOTP,
  verifyOTP,
  handleEditPhone,
}) {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef(null);

  // Guard ref to prevent infinite API call loops
  const lastSubmittedCodeRef = useRef("");

  const OTP_LENGTH = 6;

  // A fresh code invalidates whatever is sitting in the SMS thread, so say so
  // until the user starts typing again — otherwise the old code looks valid and
  // the API answers "کد نامعتبر است".
  const showReplacedHint = codeReplaced && code.length === 0;

  const handleResendClick = () => {
    setCode("");
    lastSubmittedCodeRef.current = "";
    requestOTP(true);
  };

  // Auto-submit OTP code ONCE when 6 digits are entered
  useEffect(() => {
    document.title = "ورود و ثبت نام";

    if (
      step === 2 &&
      code.length === OTP_LENGTH &&
      !loading &&
      !locked &&
      lastSubmittedCodeRef.current !== code
    ) {
      lastSubmittedCodeRef.current = code;
      verifyOTP();
    }
  }, [code, step, loading, locked, verifyOTP]);

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleCodeChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    if (val !== code) {
      lastSubmittedCodeRef.current = ""; // Reset submission guard when user edits input
      setCode(val);
    }
  };

  return (
    <Container maxWidth="xs" sx={{ mt: { xs: 6, md: 12 }, direction: "rtl" }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 5,
          p: { xs: 3, sm: 4 },
          background: "#ffffff",
          border: "1px solid",
          borderColor: "grey.100",
          boxShadow: "0px 24px 48px rgba(0, 0, 0, 0.06)",
          overflow: "hidden",
        }}
      >
        {/* LOGO / BRANDING AREA */}
        <Box display="flex" justifyContent="center" mb={3}>
          <Box
            sx={{
              width: 56,
              height: 56,
              bgcolor: "error.50",
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ef4056",
              boxShadow: "0px 8px 16px rgba(239, 64, 86, 0.15)",
            }}
          >
            {step === 1 ? (
              <PhoneAndroidOutlined fontSize="medium" />
            ) : (
              <LoginOutlined fontSize="medium" />
            )}
          </Box>
        </Box>

        {/* HEADER BRANDING */}
        <Box mb={4} textAlign="center">
          {/* Back to Home Button */}
          <Box display="flex" justifyContent="center" mb={2}>
            <Button
              component={Link}
              to="/"
              size="small"
              startIcon={<ArrowForwardRounded fontSize="small" />}
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                fontSize: "0.85rem",
                borderRadius: 2,
                px: 1.5,
                "&:hover": {
                  color: "#ef4056",
                  bgcolor: "rgba(239, 64, 86, 0.04)",
                },
              }}
            >
              بازگشت به صفحه اصلی
            </Button>
          </Box>

          {/* Clickable Brand Title */}
          <Typography
            component={Link}
            to="/"
            variant="h5"
            fontWeight={800}
            color="text.primary"
            gutterBottom
            sx={{
              textDecoration: "none",
              display: "inline-block",
              transition: "color 0.2s",
              "&:hover": { color: "#ef4056" },
            }}
          >
            ورود | ثبت‌نام
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 2, mt: 0.5 }}
          >
            {step === 1
              ? "لطفا شماره موبایل خود را وارد کنید تا کد تایید برای شما ارسال شود."
              : "کد تایید پیامک شده را وارد کنید."}
          </Typography>
        </Box>

        {/* ERROR HANDLER */}
        <Box sx={{ minHeight: error ? 60 : 0, transition: "min-height 0.3s" }}>
          <Fade in={!!error}>
            <Alert
              severity="error"
              variant="filled"
              sx={{
                mb: 3,
                borderRadius: 3,
                fontSize: "0.85rem",
                alignItems: "center",
              }}
            >
              {error}
            </Alert>
          </Fade>
        </Box>

        <Stack spacing={3}>
          {/* STEP 1: ENTER PHONE NUMBER */}
          {step === 1 && (
            <Fade in={step === 1} timeout={400}>
              {/* <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}> */}
              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!loading && phone && phone.length >= 10) {
                    requestOTP();
                  }
                }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                <TextField
                  variant="outlined"
                  label="شماره همراه"
                  placeholder="9123456789"
                  value={phone}
                  onChange={(e) => {
                    let cleanNumber = e.target.value.replace(/[^0-9]/g, "");

                    if (cleanNumber.startsWith("0")) {
                      cleanNumber = cleanNumber.substring(1);
                    }

                    setPhone(cleanNumber);
                  }}
                  fullWidth
                  autoFocus
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      transformOrigin: "right top",
                      left: "auto",
                      right: 18,
                      transform: "translate(14px, -9px) scale(0.75)",
                      bgcolor: "#ffffff",
                      px: 0.5,
                    },
                  }}
                  InputProps={{
                    dir: "ltr",
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mr: 1, ml: 0 }}>
                        <Typography
                          color="text.secondary"
                          fontWeight={600}
                          dir="ltr"
                          sx={{ fontSize: "0.95rem" }}
                        >
                          +98
                        </Typography>
                        <Box
                          component="span"
                          sx={{
                            mx: 1.5,
                            width: "1px",
                            height: 22,
                            bgcolor: "grey.300",
                            display: "inline-block",
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    // 11 so a pasted "09123456789" survives; the leading zero is
                    // stripped in onChange, leaving the 10 digits we submit.
                    maxLength: 11,
                    type: "tel",
                    inputMode: "numeric",
                    style: {
                      direction: "ltr",
                      textAlign: "left",
                      letterSpacing: "1px",
                      fontSize: "1rem",
                      fontWeight: 600,
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      height: 56,
                      bgcolor: "#ffffff",
                      transition: "all 0.2s",
                      "& fieldset": {
                        borderColor: "grey.300",
                        textAlign: "right",
                      },
                      "&:hover fieldset": { borderColor: "grey.400" },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ef4056",
                        borderWidth: "1.5px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "text.secondary",
                      fontWeight: 600,
                      right: 18,
                      "&.Mui-focused": { color: "#ef4056" },
                    },
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  // onClick={requestOTP}
                  disabled={loading || !phone || phone.length < 10}
                  fullWidth
                  disableElevation
                  sx={{
                    bgcolor: "#ef4056",
                    color: "#ffffff",
                    height: 56,
                    borderRadius: 3,
                    fontWeight: 800,
                    fontSize: "1rem",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: "#d73247",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 20px rgba(239, 64, 86, 0.3)",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "grey.100",
                      color: "grey.400",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "دریافت کد تایید"
                  )}
                </Button>
              </Box>
            </Fade>
          )}

          {/* STEP 2: ENTER OTP CODE */}
          {step === 2 && (
            <Fade in={step === 2} timeout={400}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Phone Notification Banner */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: "grey.50",
                    p: 1.5,
                    px: 2,
                    borderRadius: 2.5,
                    border: "1px solid",
                    borderColor: "grey.200",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    کد تایید به شماره{" "}
                    <Box
                      component="span"
                      fontWeight={700}
                      color="text.primary"
                      dir="ltr"
                      sx={{ display: "inline-block", mx: 0.5 }}
                    >
                      +98 {phone}
                    </Box>{" "}
                    ارسال شد
                  </Typography>

                  <IconButton
                    onClick={handleEditPhone}
                    size="small"
                    sx={{
                      color: "#ef4056",
                      bgcolor: "rgba(239, 64, 86, 0.08)",
                      "&:hover": { bgcolor: "rgba(239, 64, 86, 0.15)" },
                    }}
                  >
                    <EditRounded fontSize="small" />
                  </IconButton>
                </Box>

                {/* SEGMENTED OTP INPUT */}
                <Box
                  position="relative"
                  onClick={() => {
                    if (!locked) inputRef.current?.focus();
                  }}
                  sx={{ cursor: locked ? "not-allowed" : "pointer" }}
                >
                  <input
                    ref={inputRef}
                    autoFocus
                    type="tel"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={OTP_LENGTH}
                    value={code}
                    disabled={locked}
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => setIsInputFocused(false)}
                    onChange={handleCodeChange}
                    style={{
                      position: "absolute",
                      opacity: 0,
                      width: "100%",
                      height: "100%",
                      zIndex: 2,
                      cursor: locked ? "not-allowed" : "pointer",
                    }}
                  />

                  {/* Digit Display Stack */}
                  <Stack
                    direction="row"
                    spacing={{ xs: 1, sm: 1.5 }}
                    justifyContent="center"
                    dir="ltr"
                  >
                    {Array.from({ length: OTP_LENGTH }).map((_, index) => {
                      const digit = code[index] || "";
                      const isActive =
                        isInputFocused &&
                        (code.length === index ||
                          (index === OTP_LENGTH - 1 &&
                            code.length === OTP_LENGTH));

                      return (
                        <Box
                          key={index}
                          sx={{
                            width: { xs: 46, sm: 52 },
                            height: { xs: 46, sm: 52 },
                            borderRadius: 2,
                            border: "1.5px solid",
                            borderColor: isActive
                              ? "#ef4056"
                              : digit
                                ? "text.primary"
                                : "grey.300",
                            bgcolor: isActive ? "#ffffff" : "#f8f9fa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: { xs: "1.25rem", sm: "1.4rem" },
                            fontWeight: 800,
                            color: "text.primary",
                            boxShadow: isActive
                              ? "0 0 0 3px rgba(239, 64, 86, 0.15)"
                              : "none",
                            transition: "all 0.15s ease-in-out",
                          }}
                        >
                          {digit}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>

                {/* Resend & Timer Zone */}
                <Box textAlign="center" sx={{ minHeight: 28 }}>
                  {showReplacedHint && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      کد جدید ارسال شد؛ کد قبلی دیگر معتبر نیست.
                    </Typography>
                  )}
                  {locked ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: "0.85rem",
                      }}
                    >
                      <TimerOutlined fontSize="small" sx={{ color: "grey.500" }} />
                      <span>{formatTimer(lockSeconds)}</span>
                      <span>تا پایان محدودیت تلاش‌های ناموفق صبر کنید</span>
                    </Typography>
                  ) : timer > 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: "0.85rem",
                      }}
                    >
                      <TimerOutlined
                        fontSize="small"
                        sx={{ color: "grey.500" }}
                      />
                      <span>{formatTimer(timer)}</span>
                      <span>تا درخواست کد جدید</span>
                    </Typography>
                  ) : (
                    <Button
                      onClick={handleResendClick}
                      disabled={loading}
                      sx={{
                        color: "#ef4056",
                        fontWeight: 700,
                        fontSize: "0.85rem",
                        "&:hover": { bgcolor: "rgba(239, 64, 86, 0.04)" },
                      }}
                    >
                      ارسال کد جدید
                    </Button>
                  )}
                </Box>

                {/* STEP 2 SUBMIT BUTTON */}
                <Button
                  variant="contained"
                  onClick={verifyOTP}
                  disabled={loading || locked || code.length < OTP_LENGTH}
                  fullWidth
                  disableElevation
                  sx={{
                    bgcolor: "#ef4056",
                    color: "#ffffff",
                    height: 56,
                    borderRadius: 3,
                    fontWeight: 800,
                    fontSize: "1rem",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: "#d73247",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 20px rgba(239, 64, 86, 0.3)",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "grey.100",
                      color: "grey.400",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "تایید و ورود"
                  )}
                </Button>
              </Box>
            </Fade>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}
